import * as React from 'react';
import { clsx } from 'clsx';
import { MoreVertical } from 'lucide-react';
import { Checkbox } from '../Checkbox';

export interface TableColumn {
  /**
   * Unique key for the column
   */
  key: string;
  /**
   * Column header label
   */
  label: string;
  /**
   * Whether to show the menu icon in the header
   */
  showMenu?: boolean;
  /**
   * Custom width for the column
   */
  width?: string;
}

export interface TableRow {
  /**
   * Unique ID for the row
   */
  id: string;
  /**
   * Cell data keyed by column key
   */
  cells: Record<string, React.ReactNode>;
  /**
   * Whether the row is selected
   */
  selected?: boolean;
}

export interface TableProps {
  /**
   * Table columns configuration
   */
  columns: TableColumn[];
  /**
   * Table rows data
   */
  rows: TableRow[];
  /**
   * Callback when row selection changes
   */
  onRowSelectionChange?: (rowId: string, selected: boolean) => void;
  /**
   * Callback when select all is toggled
   */
  onSelectAllChange?: (selected: boolean) => void;
  /**
   * Whether all rows are selected
   */
  allSelected?: boolean;
  /**
   * Whether some rows are selected (indeterminate state)
   */
  someSelected?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const tableStyles: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const headerCellStyles: React.CSSProperties = {
  height: '44px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: 0,
  paddingBottom: 0,
  backgroundColor: '#f8f8f8',
  borderBottom: '1px solid #efefef',
  fontSize: '13px',
  fontWeight: 600,
  color: '#2f2f2f',
  textAlign: 'left',
  position: 'relative',
};

const checkboxCellStyles: React.CSSProperties = {
  width: '48px',
  paddingLeft: '16px',
  paddingRight: '8px',
};

const bodyCellStyles: React.CSSProperties = {
  height: '41px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
  borderBottom: '1px solid #efefef',
  fontSize: '13px',
  color: '#2f2f2f',
};

const menuButtonStyles: React.CSSProperties = {
  width: '24px',
  height: '24px',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  marginLeft: '8px',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease-in-out',
};

const rowStyles: React.CSSProperties = {
  transition: 'background-color 0.1s ease-in-out',
};

/**
 * Table component - Arbor Design System
 *
 * A data table component with selectable rows and column headers.
 * Use with TableContainer to wrap TableControls, Table, and TableFooterPagination together.
 */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      columns,
      rows,
      onRowSelectionChange,
      onSelectAllChange,
      allSelected = false,
      someSelected = false,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
    const [hoveredMenuButton, setHoveredMenuButton] = React.useState<string | null>(null);

    return (
      <table
        ref={ref}
        className={clsx('arbor-table', className)}
        style={{ ...tableStyles, ...style }}
        data-testid={dataTestId}
      >
        <thead>
          <tr>
            {/* Checkbox header cell */}
            <th style={{ ...headerCellStyles, ...checkboxCellStyles }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={onSelectAllChange}
                aria-label="Select all rows"
              />
            </th>

            {/* Column headers */}
            {columns.map((column) => (
              <th
                key={column.key}
                style={{
                  ...headerCellStyles,
                  ...(column.width ? { width: column.width } : {}),
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{column.label}</span>
                  {column.showMenu !== false && (
                    <button
                      type="button"
                      onMouseEnter={() => setHoveredMenuButton(`header-${column.key}`)}
                      onMouseLeave={() => setHoveredMenuButton(null)}
                      style={{
                        ...menuButtonStyles,
                        backgroundColor:
                          hoveredMenuButton === `header-${column.key}` ? '#f5f5f5' : 'transparent',
                      }}
                      aria-label={`${column.label} options`}
                    >
                      <MoreVertical size={16} color="#595959" />
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => {
            const isOdd = index % 2 === 0;
            const isHovered = hoveredRow === row.id;

            return (
              <tr
                key={row.id}
                onMouseEnter={() => setHoveredRow(row.id)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{
                  ...rowStyles,
                  backgroundColor: isHovered
                    ? '#fafafa'
                    : isOdd
                      ? 'white'
                      : '#f8f8f8',
                }}
              >
                {/* Checkbox cell */}
                <td style={{ ...bodyCellStyles, ...checkboxCellStyles }}>
                  <Checkbox
                    checked={row.selected || false}
                    onChange={(checked) => onRowSelectionChange?.(row.id, checked)}
                    aria-label={`Select row ${row.id}`}
                  />
                </td>

                {/* Data cells */}
                {columns.map((column) => (
                  <td key={column.key} style={bodyCellStyles}>
                    {row.cells[column.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
);

Table.displayName = 'Table';
